import * as THREE from "three";
import { Lod } from "./views/lod";
import { Cell } from "@highmarch/domain";
import { CpuRelease } from "./cpuRelease";
import { buildTowerModel, surfaceY } from "./views";
import { TowerIdle } from "./views/towerIdle";
import { HUB_BUDGET } from "./hubBudget";
import { HubLayout } from "./hubLayout";
import type { HubModel } from "./hubFx";

/**
 * Reduced-detail fortress and towers for the hub (HUB_BUDGET.ModelLod). The models are the level's, cloned from the
 * library with its geometry shared; each large mesh of the clone is pointed at a reduced copy instead (lod.ts caches
 * the reduction per source geometry, so a second visit to the hub only copies buffers). Every attribute is carried
 * over per kept vertex — skin indices and weights included — so the rigid rigs and their clips animate unchanged:
 * the simplifier only collapses edges, and the rig's parts share none, so no part is ever welded to another.
 *
 * Returns the copies made, which the caller owns and disposes (the library's geometry is untouched). They are
 * draw-only, so their arrays are released after upload (cpuRelease.ts, M31). Before the
 * simplifier has compiled (Lod.ready) nothing is reduced and the models stand at full detail.
 */
export function reduceModel(root: THREE.Object3D, lod: { readonly ratio: number; readonly error: number }, minTriangles: number): THREE.BufferGeometry[] {
  const made: THREE.BufferGeometry[] = [];
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh || !mesh.geometry?.index || (mesh as THREE.InstancedMesh).isInstancedMesh || Array.isArray(mesh.material)) return;
    const geo = mesh.geometry;
    if (geo.index!.count / 3 < minTriangles || geo.groups.length > 1 || Object.keys(geo.morphAttributes).length > 0) return;
    if (Object.values(geo.attributes).some((a) => (a as THREE.InterleavedBufferAttribute).isInterleavedBufferAttribute)) return;
    const r = Lod.reduction(geo, geo, lod.ratio, lod.error);
    if (!r) return;
    const copy = Lod.apply(geo, r);
    copy.name = `${geo.name}~hub`;
    mesh.geometry = copy;
    // draw-only: its arrays go once the main canvas has it (cpuRelease.ts); a rigid rig measures on the full detail
    if ((mesh as THREE.SkinnedMesh).isSkinnedMesh) CpuRelease.boundsFromFull(mesh as THREE.SkinnedMesh, geo);
    CpuRelease.geometry(copy);
    made.push(copy);
  });
  return made;
}

/** A tower standing in the hub: which archetype, on which stand. */
export interface HubStand extends HubModel { readonly id: string; readonly cell: Cell; readonly idle?: TowerIdle | null }

/**
 * The fortress or a tower as the hub stands it on `cell`: the level's model at hub detail (`reduceModel`; the copies made
 * go on `reduced`, which the hub disposes), a display tower's watchful idle (views/towerIdle.ts), and its model name.
 */
export function placeHubModel(archId: string, isBase: boolean, tier: number, cell: Cell, reduced: THREE.BufferGeometry[]): HubModel & { idle: TowerIdle | null; name: string } {
  const model = buildTowerModel(archId, isBase, tier);
  model.storeys.position.set(cell.x, surfaceY(), cell.y);
  reduced.push(...reduceModel(model.storeys, HUB_BUDGET.ModelLod, HUB_BUDGET.LodMinTriangles));
  // a display tower keeps watch: its weapon looks about (views/towerIdle.ts)
  const idle = isBase ? null : new TowerIdle(model, archId, cell.x * 131 + cell.y * 17);
  return { root: model.storeys, rig: model.rig, box: HubLayout.modelBox(model.storeys), idle, name: model.name };
}
